/**
 * StatisticsPanel Component - Display project statistics
 */
import React, { useEffect, useState } from 'react';
import { useProjectStore } from '../../stores';
import axios from 'axios';
import '../Dialogs/Dialog.css';

// Get API URL from environment
const API_URL = process.env.REACT_APP_API_URL && process.env.REACT_APP_API_URL !== '' ? process.env.REACT_APP_API_URL : '/api';

// Helper function to get auth token
const getAuthToken = (): string | null => {
  const authStorage = localStorage.getItem('auth-storage');
  if (authStorage) {
    try {
      const { state } = JSON.parse(authStorage);
      return state?.token || null;
    } catch (error) {
      console.error('Error parsing auth storage:', error);
      return null;
    }
  }
  return null;
};

interface Statistics {
  total_poles: number;
  total_cables: number;
  total_paths: number;
  olt_count: number;
  pole_count: number;
  manhole_count: number;
  splitter_count: number;
  endpoint_count: number;
  total_cable_length: number;
  network_health: string;
}

export const StatisticsPanel: React.FC = () => {
  const { currentProject } = useProjectStore();
  const [stats, setStats] = useState<Statistics | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (currentProject) {
      fetchStatistics();
    }
  }, [currentProject]);

  const fetchStatistics = async () => {
    if (!currentProject) return;

    setLoading(true);
    try {
      const token = getAuthToken();
      const response = await axios.get(
        `${API_URL}/projects/${currentProject.id}/statistics`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!currentProject) {
    return (
      <div className="stats-panel">
        <h3>Štatistika</h3>
        <p style={{ color: '#999', fontSize: '14px' }}>Nie je vybraný projekt</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="stats-panel">
        <h3>Štatistika</h3>
        <p style={{ color: '#999', fontSize: '14px' }}>Načítava sa...</p>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="stats-panel">
        <h3>Štatistika</h3>
        <p style={{ color: '#999', fontSize: '14px' }}>Žiadne dáta</p>
      </div>
    );
  }

  const getHealthColor = (health: string) => {
    switch (health) {
      case 'healthy':
        return '#2e7d32';
      case 'warning':
        return '#ef6c00';
      case 'error':
        return '#c62828';
      default:
        return '#999';
    }
  };

  const getHealthIcon = (health: string) => {
    switch (health) {
      case 'healthy':
        return '✓';
      case 'warning':
        return '⚠';
      case 'error':
        return '✕';
      default:
        return '?';
    }
  };

  return (
    <div className="stats-panel">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ margin: 0 }}>Štatistika</h3>
        <button
          onClick={fetchStatistics}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            fontSize: '18px',
            padding: '4px 8px',
          }}
          title="Obnoviť"
        >
          🔄
        </button>
      </div>

      <div className="stats-grid">
        <div className="stat-item">
          <div className="stat-label">Celkom bodov</div>
          <div className="stat-value">{stats.total_poles || 0}</div>
        </div>

        <div className="stat-item">
          <div className="stat-label">Celkom káblov</div>
          <div className="stat-value">{stats.total_cables || 0}</div>
        </div>

        <div className="stat-item">
          <div className="stat-label">Stĺpikov</div>
          <div className="stat-value">{stats.pole_count || 0}</div>
        </div>

        <div className="stat-item">
          <div className="stat-label">Šácht</div>
          <div className="stat-value">{stats.manhole_count || 0}</div>
        </div>

        <div className="stat-item">
          <div className="stat-label">OLT zariadení</div>
          <div className="stat-value">{stats.olt_count || 0}</div>
        </div>

        <div className="stat-item">
          <div className="stat-label">Splittrov</div>
          <div className="stat-value">{stats.splitter_count || 0}</div>
        </div>

        <div className="stat-item">
          <div className="stat-label">Koncových bodov</div>
          <div className="stat-value">{stats.endpoint_count || 0}</div>
        </div>

        <div className="stat-item">
          <div className="stat-label">Optických ciest</div>
          <div className="stat-value">{stats.total_paths || 0}</div>
        </div>

        <div className="stat-item" style={{ gridColumn: '1 / -1' }}>
          <div className="stat-label">Celková dĺžka káblov</div>
          <div className="stat-value">
            {(stats.total_cable_length || 0).toFixed(2)}
            <span className="stat-unit">m</span>
          </div>
        </div>

        <div className="stat-item" style={{ gridColumn: '1 / -1' }}>
          <div className="stat-label">Stav siete</div>
          <div
            className="stat-value"
            style={{
              color: getHealthColor(stats.network_health || 'unknown'),
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <span>{getHealthIcon(stats.network_health || 'unknown')}</span>
            <span style={{ fontSize: '18px', textTransform: 'capitalize' }}>
              {stats.network_health === 'healthy' ? 'V poriadku' :
               stats.network_health === 'warning' ? 'Upozornenie' :
               stats.network_health === 'error' ? 'Chyba' : 'Neznámy'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

